/**
 * 거래처(클라이언트) 목록 — 레거시 `clientInfo.js` UI·필터·업로드 포팅.
 */
import type {
  BusinessRelationshipResponse,
  BusinessRelationshipUploadItem,
} from '@/entities/business/api/business.api';
import {
  useBulkUpdateBusinessRelationshipTransactionStatus,
  useBusinessRelationshipList,
  useExportBusinessRelationships,
  useUploadBusinessRelationships,
} from '@/entities/business/hooks/useBusinessRelationships';
import { usePartnerCompanyOptions } from '@/entities/partner-company/hooks/usePartnerCompanies';
import {
  BUSINESS_BULK_STATUS_MAX_TARGETS,
  buildBulkStatusUpdateRequest,
  clientRowSelectionKey,
  formatBulkStatusResultMessage,
} from '@/pages/clients/clientBulkStatusUtils';
import {
  PHARMA_TRANSACTION_STATUS_OPTIONS,
  matchesContractStatus,
  type PharmaTransactionStatus,
} from '@/pages/clients/clientStatusUtils';
import {
  BUSINESS_EXPORT_HEADER_MAP,
  downloadBusinessUploadTemplate,
  parseBusinessUploadXlsx,
} from '@/pages/clients/clientUploadUtils';
import {
  CONTRACT_STATUS_RADIO_OPTIONS,
  displayPartnerCompanyName,
  formatDate,
  formatRelatedPharmacyLabel,
  getBusinessRelationshipCompanyErrorMessage,
  getClinicTypeLabel,
  getDrugApprovalStatusColor,
  getDrugApprovalStatusLabel,
  getTransactionStatusColor,
  mapContractStatusToDrugApproval,
  formatStatusLabel,
} from '@/pages/clients/clientUtils';
import { DataCard } from '@/shared/components/DataCard';
import { EmptyState } from '@/shared/components/EmptyState';
import { FileUploadButton } from '@/shared/components/FileUploadButton';
import { FilterCard } from '@/shared/components/FilterCard';
import { LoadingSpinner } from '@/shared/components/LoadingSpinner';
import { MenuWriteGate } from '@/shared/components/MenuWriteGate';
import { PageHeader } from '@/shared/components/PageHeader';
import { SimplePagination } from '@/shared/components/SimplePagination';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/components/ui/alert-dialog';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import { Checkbox } from '@/shared/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Field } from '@/shared/components/ui/form-field';
import { Input } from '@/shared/components/ui/input';
import { RadioGroup, RadioGroupItem } from '@/shared/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import { FilterRow, Stack, ToolbarRow } from '@/shared/components/ui/row';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui/table';
import { clientDetailPath } from '@/shared/constants/routes';
import { useListSearchParams } from '@/shared/hooks/useListSearchParams';
import { cn } from '@/shared/lib/utils';
import { isFacilityClosed } from '@/shared/utils/facilityClosure';
import { handleApiError, useToast } from '@/shared/utils/toast';
import { downloadXlsx } from '@/shared/utils/xlsxExport';
import { useNavigate } from '@tanstack/react-router';
import { Download, Handshake, Upload } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';

const CLIENT_SEARCH_DEFAULTS = {
  page: 1,
  pageSize: 25,
  clinicName: '',
  pharmaCompanyName: '',
  email: '',
  ceoName: '',
  companyName: '',
  parentCompanyId: '',
  settlementCompanyId: '',
  transactionStatus: '',
  drugApprovalStatus: '',
};

const toOptionalCompanyId = (value: string) => {
  const id = Number(value);
  return value && Number.isFinite(id) && id > 0 ? id : undefined;
};

const StatusBadge: React.FC<{ label: string; color: string }> = ({
  label,
  color,
}) => (
  <Badge
    variant="secondary"
    className="border-0 font-medium text-white"
    style={{ backgroundColor: color }}
  >
    {label}
  </Badge>
);

export const ClientsPage: React.FC = () => {
  const toast = useToast();
  const navigate = useNavigate();

  const { values, setValues } = useListSearchParams(CLIENT_SEARCH_DEFAULTS);

  const { data: partnerCompanyOptions = [] } = usePartnerCompanyOptions();

  const [clinicInput, setClinicInput] = useState(values.clinicName);
  const [pharmaInput, setPharmaInput] = useState(values.pharmaCompanyName);
  const [emailInput, setEmailInput] = useState(values.email);
  const [ceoNameInput, setCeoNameInput] = useState(values.ceoName);
  const [companyNameInput, setCompanyNameInput] = useState(
    values.companyName,
  );
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [bulkStatus, setBulkStatus] =
    useState<PharmaTransactionStatus>('승인');
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [bulkUpdating, setBulkUpdating] = useState(false);

  useEffect(() => {
    setClinicInput(values.clinicName);
    setPharmaInput(values.pharmaCompanyName);
    setEmailInput(values.email);
    setCeoNameInput(values.ceoName);
    setCompanyNameInput(values.companyName);
  }, [
    values.clinicName,
    values.pharmaCompanyName,
    values.email,
    values.ceoName,
    values.companyName,
  ]);

  const search = useMemo(
    () => ({
      clinicName: values.clinicName || undefined,
      pharmaCompanyName: values.pharmaCompanyName || undefined,
      email: values.email || undefined,
      ceoName: values.ceoName || undefined,
      companyName: values.companyName || undefined,
      transactionStatus: values.transactionStatus || undefined,
      drugApprovalStatus: values.drugApprovalStatus || undefined,
      // 서버가 아직 안 받아도 선택 값은 쿼리에 붙인다
      parentCompanyId: toOptionalCompanyId(values.parentCompanyId),
      settlementCompanyId: toOptionalCompanyId(values.settlementCompanyId),
    }),
    [values],
  );

  const pageable = useMemo(
    () => ({ page: Math.max(0, values.page - 1), size: values.pageSize }),
    [values.page, values.pageSize],
  );

  const { data, isLoading, isError, error, refetch } =
    useBusinessRelationshipList({
      search,
      pageable,
    });

  const exportClients = useExportBusinessRelationships();
  const uploadClients = useUploadBusinessRelationships();
  const bulkStatusMutation = useBulkUpdateBusinessRelationshipTransactionStatus();
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadItems, setUploadItems] = useState<
    BusinessRelationshipUploadItem[]
  >([]);

  useEffect(() => {
    if (isError && error) {
      handleApiError(error, toast);
    }
  }, [isError, error, toast]);

  // 서버가 거래 상태 OR 의약품 상태로 찾아 오므로, 종별로 화면에 보이는 상태가 맞는 행만 남긴다.
  // 걸러진 행만큼 페이지 행 수가 줄 수 있고, 전체 건수·페이지 수는 서버 값 그대로다.
  const clients = useMemo(
    () =>
      (data?.content ?? []).filter((client) =>
        matchesContractStatus(client, values.transactionStatus),
      ),
    [data, values.transactionStatus],
  );
  const totalCount = data?.totalCount ?? 0;
  const pageKeys = useMemo(
    () => clients.map((client) => clientRowSelectionKey(client)),
    [clients],
  );
  const allPageSelected =
    pageKeys.length > 0 && pageKeys.every((key) => selectedKeys.includes(key));

  // 페이지·검색 결과가 바뀌면 현재 페이지에 없는 선택은 버린다
  useEffect(() => {
    setSelectedKeys((prev) => prev.filter((key) => pageKeys.includes(key)));
  }, [pageKeys]);

  const selectedClients = useMemo(
    () =>
      clients.filter((client) =>
        selectedKeys.includes(clientRowSelectionKey(client)),
      ),
    [clients, selectedKeys],
  );

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setValues({
      clinicName: clinicInput.trim(),
      pharmaCompanyName: pharmaInput.trim(),
      email: emailInput.trim(),
      ceoName: ceoNameInput.trim(),
      companyName: companyNameInput.trim(),
      page: 1,
    });
  };

  /** 레거시: 라디오 변경 시 transactionStatus + drugApprovalStatus 동시 반영 */
  const handleContractStatusChange = (status: string) => {
    const nextStatus = status === 'all' ? '' : status;
    setValues({
      transactionStatus: nextStatus,
      drugApprovalStatus: mapContractStatusToDrugApproval(nextStatus),
      page: 1,
    });
  };

  const toggleRow = (client: BusinessRelationshipResponse) => {
    const key = clientRowSelectionKey(client);
    setSelectedKeys((prev) =>
      prev.includes(key) ? prev.filter((item) => item !== key) : [...prev, key],
    );
  };

  const toggleAllPage = () => {
    if (allPageSelected) {
      setSelectedKeys((prev) => prev.filter((key) => !pageKeys.includes(key)));
      return;
    }
    setSelectedKeys((prev) => [...new Set([...prev, ...pageKeys])]);
  };

  const resetUploadState = () => {
    setUploadItems([]);
  };

  const handleUploadFile = async (file: File) => {
    try {
      const items = await parseBusinessUploadXlsx(file);
      setUploadItems(items);
    } catch (e) {
      handleApiError(e, toast);
      setUploadItems([]);
    }
  };

  const handleUpload = async () => {
    if (uploadItems.length === 0) {
      toast.error('업로드할 데이터가 없습니다.');
      return;
    }
    try {
      await uploadClients.mutateAsync(uploadItems);
      toast.success('거래처 정보 업로드 완료');
      setUploadOpen(false);
      resetUploadState();
      await refetch();
    } catch (e) {
      toast.error(getBusinessRelationshipCompanyErrorMessage(e));
    }
  };

  const handleBulkStatusUpdate = async () => {
    if (selectedClients.length === 0) return;
    if (selectedClients.length > BUSINESS_BULK_STATUS_MAX_TARGETS) {
      toast.error(
        `한 번에 최대 ${BUSINESS_BULK_STATUS_MAX_TARGETS.toLocaleString()}건까지 변경할 수 있습니다.`,
      );
      return;
    }

    setBulkUpdating(true);
    try {
      const result = await bulkStatusMutation.mutateAsync(
        buildBulkStatusUpdateRequest(selectedClients, bulkStatus),
      );
      setSelectedKeys([]);
      setBulkConfirmOpen(false);
      await refetch();

      const message = formatBulkStatusResultMessage(result);
      if (result.failedCount === 0) {
        toast.success(message);
      } else if (result.successCount === 0) {
        toast.error(message);
      } else {
        toast.error(message);
      }
    } catch (e) {
      handleApiError(e, toast);
    } finally {
      setBulkUpdating(false);
    }
  };

  const contractRadioValue = values.transactionStatus || 'all';

  return (
    <div className="space-y-5">
      <PageHeader
        icon={Handshake}
        title="거래처 정보 관리"
        actions={
          <>
            <MenuWriteGate>
              <Button
                type="button"
                className="ruda-button"
                onClick={() => {
                  resetUploadState();
                  setUploadOpen(true);
                }}
              >
                <Upload className="mr-2 h-4 w-4" />
                거래처 등록
              </Button>
            </MenuWriteGate>
            <Button
              type="button"
              variant="outline"
              disabled={exportClients.isPending}
              onClick={async () => {
                try {
                  const rows = await exportClients.mutateAsync();
                  downloadXlsx(
                    rows as unknown as Record<string, unknown>[],
                    '거래처_전체.xlsx',
                    BUSINESS_EXPORT_HEADER_MAP,
                  );
                  toast.success('거래처 데이터를 다운로드했습니다.');
                } catch (e) {
                  handleApiError(e, toast);
                }
              }}
            >
              <Download className="mr-2 h-4 w-4" />
              다운로드
            </Button>
          </>
        }
      />

      <FilterCard>
        <Stack as="form" gap={16} onSubmit={handleSearch}>
          <FilterRow as="div" gap={12}>
            <Field label="병의원명" htmlFor="clinic-name">
              <Input
                id="clinic-name"
                value={clinicInput}
                onChange={(e) => setClinicInput(e.target.value)}
                className="w-40"
              />
            </Field>
            <Field label="제약사명" htmlFor="pharma-name">
              <Input
                id="pharma-name"
                value={pharmaInput}
                onChange={(e) => setPharmaInput(e.target.value)}
                className="w-40"
              />
            </Field>
            <Field label="사용자 이메일" htmlFor="client-email">
              <Input
                id="client-email"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                className="w-48"
              />
            </Field>
            <Field label="대표자명" htmlFor="ceo-name">
              <Input
                id="ceo-name"
                value={ceoNameInput}
                onChange={(e) => setCeoNameInput(e.target.value)}
                className="w-36"
              />
            </Field>
            <Field label="업체명" htmlFor="company-name">
              <Input
                id="company-name"
                value={companyNameInput}
                onChange={(e) => setCompanyNameInput(e.target.value)}
                className="w-40"
              />
            </Field>
            <Field label="상위업체">
              <Select
                value={values.parentCompanyId || 'all'}
                onValueChange={(value) =>
                  setValues({
                    parentCompanyId: value === 'all' ? '' : value,
                    page: 1,
                  })
                }
              >
                <SelectTrigger className="w-44" aria-label="상위업체">
                  <SelectValue placeholder="전체" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">전체</SelectItem>
                  {partnerCompanyOptions.map((option) => (
                    <SelectItem
                      key={option.partnerCompanyId}
                      value={String(option.partnerCompanyId)}
                    >
                      {option.companyName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="정산업체">
              <Select
                value={values.settlementCompanyId || 'all'}
                onValueChange={(value) =>
                  setValues({
                    settlementCompanyId: value === 'all' ? '' : value,
                    page: 1,
                  })
                }
              >
                <SelectTrigger className="w-44" aria-label="정산업체">
                  <SelectValue placeholder="전체" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">전체</SelectItem>
                  {partnerCompanyOptions.map((option) => (
                    <SelectItem
                      key={option.partnerCompanyId}
                      value={String(option.partnerCompanyId)}
                    >
                      {option.companyName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Button type="submit" className="ruda-button">
              검색
            </Button>
          </FilterRow>

          <div className="space-y-2">
            <p className="text-sm font-medium text-ruda-text-primary">
              거래 상태
            </p>
            <RadioGroup
              value={contractRadioValue}
              onValueChange={handleContractStatusChange}
              className="flex flex-wrap gap-4"
            >
              {CONTRACT_STATUS_RADIO_OPTIONS.map((opt) => {
                const value = opt.value || 'all';
                return (
                  <label
                    key={value}
                    className="flex cursor-pointer items-center gap-2 text-sm"
                  >
                    <RadioGroupItem value={value} />
                    {opt.label}
                  </label>
                );
              })}
            </RadioGroup>
          </div>
        </Stack>
      </FilterCard>

      {!isLoading && clients.length > 0 ? (
        <MenuWriteGate>
          <ToolbarRow gap={8}>
            <Select
              value={bulkStatus}
              onValueChange={(value) =>
                setBulkStatus(value as PharmaTransactionStatus)
              }
              disabled={selectedKeys.length === 0 || bulkUpdating}
            >
              <SelectTrigger
                className="w-48"
                aria-label="일괄 변경할 거래 상태"
              >
                <SelectValue placeholder="거래 상태 선택" />
              </SelectTrigger>
              <SelectContent>
                {PHARMA_TRANSACTION_STATUS_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              type="button"
              variant="outline"
              disabled={selectedKeys.length === 0 || bulkUpdating}
              onClick={() => setBulkConfirmOpen(true)}
            >
              {bulkUpdating
                ? '변경 중...'
                : `선택한 ${selectedKeys.length}건 상태 변경`}
            </Button>
          </ToolbarRow>
        </MenuWriteGate>
      ) : null}

      <DataCard>
        {isLoading ? (
          <div className="py-16">
            <LoadingSpinner tip="거래처 목록을 불러오는 중..." />
          </div>
        ) : clients.length === 0 ? (
          <EmptyState description="검색 조건에 맞는 거래처가 없습니다." />
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <Checkbox
                      aria-label="현재 페이지 전체 선택"
                      checked={allPageSelected}
                      onCheckedChange={() => toggleAllPage()}
                    />
                  </TableHead>
                  <TableHead>사용자 이메일</TableHead>
                  <TableHead>대표자명</TableHead>
                  <TableHead>업체명</TableHead>
                  <TableHead>병의원명</TableHead>
                  <TableHead>거래 상태</TableHead>
                  <TableHead>연계 약국</TableHead>
                  <TableHead>병의원 구분</TableHead>
                  <TableHead>제약사명</TableHead>
                  <TableHead>상위업체</TableHead>
                  <TableHead>정산업체</TableHead>
                  <TableHead>의약품명</TableHead>
                  <TableHead>거래 신청 일자</TableHead>
                  <TableHead>거래 승인 일자</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {clients.map((client) => {
                  const isHospital = client.clinicType === 'HOSPITAL';
                  const applicationLabel = isHospital
                    ? formatDate(client.drugApplicationDate)
                    : formatDate(client.applicationDate);
                  const statusLabel = isHospital
                    ? getDrugApprovalStatusLabel(client.drugApprovalStatus)
                    : formatStatusLabel(client.transactionStatus);
                  const statusColor = isHospital
                    ? getDrugApprovalStatusColor(client.drugApprovalStatus)
                    : getTransactionStatusColor(client.transactionStatus);
                  const approvalLabel = isHospital
                    ? formatDate(client.drugApprovalDate)
                    : formatDate(client.approvalDate);
                  const clinicTypeLabel = getClinicTypeLabel(client.clinicType);
                  const pharmacies = client.pharmacies ?? [];
                  const rowKey = clientRowSelectionKey(client);
                  const isSelected = selectedKeys.includes(rowKey);

                  return (
                    <TableRow
                      key={rowKey}
                      className="cursor-pointer hover:bg-muted/50"
                      data-state={isSelected ? 'selected' : undefined}
                      onClick={() =>
                        navigate({
                          to: clientDetailPath(client.relationPharmaCompanyId),
                          search: {
                            ...(client.drugCode
                              ? { drugCode: client.drugCode }
                              : {}),
                            type: client.clinicType,
                          },
                        } as never)
                      }
                    >
                      <TableCell
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                      >
                        <Checkbox
                          aria-label={`${client.clinicName} 선택`}
                          checked={isSelected}
                          onCheckedChange={() => toggleRow(client)}
                        />
                      </TableCell>
                      <TableCell>{client.email || '-'}</TableCell>
                      <TableCell>{client.ceoName || '-'}</TableCell>
                      <TableCell>{client.companyName || '-'}</TableCell>
                      <TableCell className="font-medium">
                        <span className="inline-flex flex-wrap items-center gap-2">
                          {client.clinicName}
                          {isFacilityClosed(client.clinicClosedOn) ? (
                            <Badge variant="outline">폐업</Badge>
                          ) : null}
                        </span>
                      </TableCell>
                      <TableCell>
                        {statusLabel === '-' ? (
                          '-'
                        ) : (
                          <StatusBadge
                            label={statusLabel}
                            color={statusColor}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        {pharmacies.length === 0 ? (
                          '-'
                        ) : (
                          <div className="flex max-w-xs flex-wrap gap-1">
                            {pharmacies.map((pharmacy, index) => (
                              <Badge
                                key={`${pharmacy.pharmacyName}-${pharmacy.pharmacyClosedOn ?? 'open'}-${index}`}
                                variant="outline"
                                className="font-normal"
                              >
                                {formatRelatedPharmacyLabel(pharmacy)}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="secondary"
                          className={cn(
                            'border-0 text-white',
                            client.clinicType === 'HOSPITAL'
                              ? 'bg-blue-500'
                              : 'bg-green-500',
                          )}
                        >
                          {clinicTypeLabel}
                        </Badge>
                      </TableCell>
                      <TableCell>{client.pharmaCompanyName || '-'}</TableCell>
                      <TableCell>
                        {displayPartnerCompanyName(client.parentCompanyName)}
                      </TableCell>
                      <TableCell>
                        {displayPartnerCompanyName(
                          client.settlementCompanyName,
                        )}
                      </TableCell>
                      <TableCell>{client.drugName || '-'}</TableCell>
                      <TableCell>{applicationLabel}</TableCell>
                      <TableCell>{approvalLabel}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <SimplePagination
              page={values.page}
              pageSize={values.pageSize}
              total={totalCount}
              onPageChange={(page) => setValues({ page })}
              onPageSizeChange={(pageSize) => setValues({ pageSize, page: 1 })}
            />
          </>
        )}
      </DataCard>

      <Dialog
        open={uploadOpen}
        onOpenChange={(open) => {
          setUploadOpen(open);
          if (!open) resetUploadState();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>거래처 정보 업로드</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <ToolbarRow gap={8}>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  void downloadBusinessUploadTemplate();
                }}
              >
                <Download className="mr-2 h-4 w-4" />
                양식 다운로드
              </Button>
              <FileUploadButton
                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                onFilesSelected={(files) => {
                  const file = files?.[0];
                  if (file) void handleUploadFile(file);
                }}
              >
                파일 선택
              </FileUploadButton>
            </ToolbarRow>
            {uploadItems.length > 0 && (
              <p className="text-sm text-muted-foreground">
                {uploadItems.length}건 업로드 준비됨
              </p>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setUploadOpen(false);
                resetUploadState();
              }}
            >
              취소
            </Button>
            <Button
              onClick={handleUpload}
              disabled={uploadItems.length === 0 || uploadClients.isPending}
            >
              업로드
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={bulkConfirmOpen} onOpenChange={setBulkConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>거래 상태 일괄 변경</AlertDialogTitle>
            <AlertDialogDescription>
              선택한 {selectedKeys.length}건의 거래 상태를 “{bulkStatus}”(으)로
              변경합니다. 같은 거래처의 다른 의약품 줄에도 거래 상태가 함께
              반영됩니다.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkUpdating}>취소</AlertDialogCancel>
            <AlertDialogAction
              disabled={bulkUpdating}
              onClick={(e) => {
                e.preventDefault();
                void handleBulkStatusUpdate();
              }}
            >
              {bulkUpdating ? '변경 중...' : '변경'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
